import React, { useState } from 'react';
import {
  Box,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Badge,
  Button,
  Text,
  MailIcon,
  TableToolbar,
  Heading,
  ArrowUpRightIcon,
  RefreshIcon,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { useMissingInformationReport } from './hooks/useMissingInformationReport';
import { getGenderText, getTableToolbarText } from './utils';
import { useLocation } from 'react-router-dom';
import { SendReminderModal } from './SendReminderModal';
import { useSendReminder } from './hooks/useSendReminder';
import { prepareUrlV2 } from 'utils/Urls';
import { employeeOnboardingSteps, routePaths } from 'components/Routes/data';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ExportReportButton } from './components/ExportReportButton';

const TextOrMissingBadge = ({ textContent }: { textContent?: string | null }) =>
  textContent ? (
    <Text color="surface.text.gray.subtle">{textContent}</Text>
  ) : (
    <Badge color="negative">Missing</Badge>
  );

export const PersonalDetails = () => {
  const [selectedTableRowIds, setSelectedTableRowIds] = useState<(string | number)[]>([]);
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });
  const {
    isSendReminderModalOpen,
    remindEmployeeIds,
    onOpenModal,
    onCloseModal,
    sendReminder,
    isSendingReminder,
  } = useSendReminder('personal_details');

  const location = useLocation();

  const {
    data: employeeData,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useMissingInformationReport('personal_details', currentPage, itemsPerPage);

  const shouldShowEmptyView =
    !isError && !isLoading && !isFetching && employeeData?.data.items.length === 0;
  const shouldShowErrorView = isError;
  const shouldShowTable = !shouldShowEmptyView && !shouldShowErrorView;

  return (
    <Box>
      <Heading
        size="large"
        weight="semibold"
        color="surface.text.gray.normal"
        marginBottom="spacing.2">
        Personal Details
      </Heading>
      <Text color="surface.text.gray.subtle" marginBottom="spacing.7">
        Remind employees to provide employee basic details
      </Text>
      {isError && <ErrorView onRefetch={refetch} />}
      {shouldShowEmptyView && (
        <EmptyListView
          title="🔍 No Missing Information Found"
          description="Great! All employees have completed their details."
        />
      )}
      {shouldShowTable && (
        <Table
          isLoading={isLoading}
          isRefreshing={isFetching}
          data={{ nodes: employeeData?.data.items || [] }}
          onSelectionChange={({ selectedIds }) => {
            setSelectedTableRowIds(selectedIds);
          }}
          selectionType="multiple"
          multiSelectTrigger="checkbox"
          backgroundColor="surface.background.gray.intense"
          toolbar={
            <TableToolbar
              selectedTitle={`${selectedTableRowIds.length} Employees Selected`}
              title={getTableToolbarText(
                employeeData?.data.total_count || 0,
                currentPage,
                itemsPerPage,
              )}>
              <Box display="flex" justifyContent="flex-end" gap="spacing.5">
                <ExportReportButton type="personal_details" />
                <Button
                  icon={MailIcon}
                  isDisabled={selectedTableRowIds.length === 0}
                  onClick={() => {
                    onOpenModal({
                      remindEmployeeIds: selectedTableRowIds,
                    });
                  }}>
                  Send Reminder
                </Button>
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  accessibilityLabel="Refresh Personal Details"
                  onClick={refetch}
                  isDisabled={isFetching || isLoading}
                />
              </Box>
            </TableToolbar>
          }
          pagination={
            <TablePagination
              paginationType="server"
              showPageSizePicker={true}
              currentPage={currentPage}
              totalItemCount={employeeData?.data.total_count || 0}
              onPageChange={({ page }) => setCurrentPage(page)}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
            />
          }>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Phone Number</TableHeaderCell>
                  <TableHeaderCell>Gender</TableHeaderCell>
                  <TableHeaderCell>Date of Birth</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((employee) => (
                  <TableRow
                    key={employee.id}
                    item={employee}
                    hoverActions={
                      <Box display="flex" gap="spacing.4">
                        <Button
                          size="xsmall"
                          variant="tertiary"
                          icon={MailIcon}
                          iconPosition="left"
                          onClick={() => {
                            onOpenModal({
                              remindEmployeeIds: [employee.id],
                            });
                          }}>
                          Send Reminder
                        </Button>
                        <Button
                          iconPosition="left"
                          icon={ArrowUpRightIcon}
                          size="xsmall"
                          variant="secondary"
                          onClick={() => {
                            const url = prepareUrlV2(routePaths.phpRoutes.employeeOnboarding, {
                              userId: employee.id,
                              step: employeeOnboardingSteps.otherDetails,
                              redirect: location.pathname,
                            });
                            window.location.href = url;
                          }}>
                          Employee Profile
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box>
                        <Text color="surface.text.gray.subtle">{employee.name}</Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {employee.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextOrMissingBadge textContent={employee.phone} />
                    </TableCell>
                    <TableCell>
                      <TextOrMissingBadge textContent={getGenderText(employee.gender)} />
                    </TableCell>
                    <TableCell>
                      <TextOrMissingBadge textContent={employee.dob} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}

      <SendReminderModal
        isOpen={isSendReminderModalOpen}
        onClose={onCloseModal}
        description={
          remindEmployeeIds.length === 1
            ? 'RazorpayX Payroll will email this employee to remind them to fill in their personal details.'
            : "RazorpayX Payroll will email each selected person in your organization who hasn't provided their personal details."
        }
        isSendingReminder={isSendingReminder}
        onSendReminder={() => {
          sendReminder({
            employee_ids: remindEmployeeIds.map((id) => Number(id)),
            reminder_type: 'personal_details',
          });
        }}
      />
    </Box>
  );
};
